/**
 * Declarative HTMX after-request actions via data attributes.
 *
 * Ported from Loop's htmx-actions.js. Replaces inline hx-on::after-request
 * JS blobs with clean HTML attributes:
 *
 *   data-success-toast="msg"    → showToast(msg, 'success') or type from data-success-toast-type
 *   data-success-refresh        → rfSheet.refresh() in sheet, page reload on full page
 *   data-success-close-sheet    → rfSheet.close()
 *   data-success-reload         → window.location.reload()
 *   data-success-redirect="url" → window.location.href = url
 *   data-success-reset          → form.reset()
 *   data-success-remove="id"    → remove element by ID
 *   data-success-close-dropdown → close the parent dropdown menu
 *
 * Attributes combine freely. Order: refresh → toast → remove → reset →
 * close-dropdown → close-sheet → reload/redirect (last, since they navigate).
 *
 * Server-sent toasts via HX-Trigger header:
 *   response['HX-Trigger'] = json.dumps({'showToast': {'message': 'Saved!', 'type': 'success'}})
 *
 * Server-sent dropdown close (for a success response with no swap to trigger the normal
 * htmx:afterSwap orphan cleanup in dropdowns.js — e.g. a 204 that can't be told apart from
 * a validation-failure 204 by status code alone, so a client-side data-success-close-dropdown
 * would misfire on both):
 *   response['HX-Trigger'] = json.dumps({'closeDropdown': true})
 *
 * Server-sent sheet close, for a write whose success decides whether the sheet survives
 * (an Unlink that deletes the buyer the sheet shows, `acqs/views/buyers.py`):
 *   response['HX-Trigger'] = json.dumps({'closeSheet': true})
 */

(function () {

    function restoreExternalSubmitButtons(form) {
        if (!form || form.tagName !== 'FORM' || !form.id) return;
        document.querySelectorAll('button[form="' + form.id + '"]').forEach(function (btn) {
            btn.disabled = false;
            btn.classList.remove('btn--loading');
            btn.removeAttribute('data-icon-loading-auto');
        });
    }

    function looksLikeSheetPayload(html) {
        return /data-sheet-(toolbar|footer|actions|title|wide|full|compact)\b/.test(html || '');
    }

    // A 4xx that explicitly sets `HX-Reswap: none` AND still returns a body is
    // sending OOB-only content: the header says "leave my target alone", so the
    // body can only be `hx-swap-oob` fragments. htmx processes those inside
    // swap(), and swap() is skipped entirely whenever shouldSwap is false — its
    // default on every error status. So that content is silently dropped on
    // anything but the 422 handled below, which is why acqs' stage endpoints
    // rendered `_deal_stage_error_oob.html` for a 422 and showed nothing for
    // their 400 ("Choose a valid deal status") / 404 ("Deal not found").
    // Forcing the swap here is safe by construction: `none` is still the swap
    // style, so the primary target is never touched and only the OOB fragments
    // land. Empty-body refusals (the 409 conflict toasts, blast/prospect field
    // validation) have nothing to swap and keep behaving exactly as before.
    function isOobOnlyRefusal(xhr) {
        return xhr.status >= 400 && xhr.status < 500 &&
            (xhr.getResponseHeader('HX-Reswap') || '').trim() === 'none' &&
            (xhr.responseText || '').trim() !== '';
    }

    document.body.addEventListener('htmx:beforeSwap', function (e) {
        var xhr = e.detail.xhr;
        if (!xhr) return;
        var status = xhr.status;

        // 422 always swaps — the server echoes the form with inline field errors.
        // 409 keeps htmx's no-swap default: most conflict responses are an empty
        // body plus an HX-Trigger toast, and swapping those would blank a target.
        if (status === 422 || isOobOnlyRefusal(xhr)) e.detail.shouldSwap = true;

        if (status !== 422 && status !== 409) return;

        // Sheet forms with hx-swap="none" would silently discard a re-rendered
        // sheet body (422 field errors, 409 conflict panel — e.g. offer-create's
        // "Open offer exists"). Route that body through rfSheet's populate
        // pipeline instead so it actually displays. Empty-body responses
        // (toast-only validation, e.g. contact-add) are untouched.
        // Caveat: hx-swap-oob fragments in the body are NOT processed on this
        // path — populateSheet treats them as plain markup.
        var el = e.detail.elt;
        var sheet = window.rfSheet && window.rfSheet.active && window.rfSheet.active();
        var swapSrc = el && el.closest && el.closest('[hx-swap]');
        if (sheet && swapSrc && swapSrc.getAttribute('hx-swap') === 'none' &&
                sheet.contains(el) && looksLikeSheetPayload(e.detail.xhr.responseText)) {
            e.detail.shouldSwap = false;
            window.rfSheet.render(e.detail.xhr.responseText);
        }
    });

    document.body.addEventListener('htmx:afterRequest', function (e) {
        var el = e.detail.elt;

        if (e.detail.xhr && e.detail.xhr.status === 422 && el.tagName === 'FORM' && el.id) {
            restoreExternalSubmitButtons(el);
        }

        if (!e.detail.successful) return;

        if (el.hasAttribute('data-success-refresh')) {
            if (window.rfSheet && window.rfSheet.active()) {
                window.rfSheet.refresh();
            } else {
                window.location.reload();
                return;
            }
        }

        var toastMsg = el.getAttribute('data-success-toast');
        if (toastMsg && window.showToast) {
            window.showToast(toastMsg, el.getAttribute('data-success-toast-type') || 'success');
        }

        var removeId = el.getAttribute('data-success-remove');
        if (removeId) {
            var target = document.getElementById(removeId);
            if (target) target.remove();
        }

        if (el.hasAttribute('data-success-reset') && el.tagName === 'FORM') {
            el.reset();
        }

        if (el.hasAttribute('data-success-close-dropdown')) {
            if (window._cc && window._cc.closeAllDropdowns) {
                window._cc.closeAllDropdowns();
            } else {
                var menu = el.closest('.dropdown-menu');
                if (menu) { menu.classList.remove('show'); var dd = menu.closest('.dropdown'); if (dd) dd.classList.remove('show'); }
            }
        }

        if (el.hasAttribute('data-success-close-sheet') && window.rfSheet) {
            window.rfSheet.close();
        }

        if (el.hasAttribute('data-success-reload')) {
            window.location.reload();
            return;
        }

        var redirectUrl = el.getAttribute('data-success-redirect');
        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    });

    document.body.addEventListener('htmx:afterSettle', function (e) {
        var form = e.detail.elt && e.detail.elt.matches && e.detail.elt.matches('form')
            ? e.detail.elt
            : e.detail.target && e.detail.target.querySelector && e.detail.target.querySelector('form');
        restoreExternalSubmitButtons(form);
    });

    document.body.addEventListener('showToast', function (e) {
        if (window.showToast && e.detail) {
            window.showToast(e.detail.message, e.detail.type || 'success');
        }
    });

    // Open detail sheet when a new record is created via HX-Trigger
    ['lead', 'owner', 'affiliate', 'firm', 'property', 'prospect'].forEach(function (type) {
        document.body.addEventListener(type + 'Created', function (e) {
            var url = e.detail && e.detail[type + 'Url'];
            if (url && window.rfSheet) {
                var hasSheetHistory = new URLSearchParams(window.location.search).has('sheet');
                window.rfSheet.open(url, { history: hasSheetHistory ? 'replace' : 'push' });
            }
        });
    });

    // Address conflict: the property already belongs to another open lead.
    // The 422 response sends HX-Trigger: {"openExistingLead": {"leadId": .., "leadUrl": ".."}}
    // alongside the error toast — open that existing lead's sheet so the user can jump to it.
    document.body.addEventListener('openExistingLead', function (e) {
        var url = e.detail && e.detail.leadUrl;
        if (url && window.rfSheet) {
            window.rfSheet.open(url);
        }
    });

    // Close open dropdowns after a server action that succeeded but returned no swap (e.g.
    // a 204 the caller can't distinguish from a validation-failure 204 by status alone).
    // Server sends HX-Trigger: {"closeDropdown": true} ONLY on the real-success path — see
    // agent/review/leasing.py::_flag_control_response for the canonical caller.
    document.body.addEventListener('closeDropdown', function () {
        if (window._cc && window._cc.closeAllDropdowns) {
            window._cc.closeAllDropdowns();
        }
    });

    // Server-sent return to the sheet this one was opened from (sheets.js
    // `rfSheet.back`): {'sheetBack': {'url': fallback}}. A form sheet's save
    // sends it after the swap, in place of reopening that sheet with openSheet.
    document.body.addEventListener('sheetBack', function (e) {
        if (window.rfSheet) window.rfSheet.back(e.detail && e.detail.url);
    });

    document.body.addEventListener('closeSheet', function () {
        if (window.rfSheet) window.rfSheet.close();
    });

    document.body.addEventListener('openSheet', function (e) {
        var url = (typeof e.detail === 'string') ? e.detail : (e.detail && (e.detail.url || e.detail.value));
        if (url && window.rfSheet) {
            var replaceHistory = e.detail && e.detail.history === 'replace';
            window.rfSheet.open(url, { history: replaceHistory ? 'replace' : 'push' });
        }
    });

    // Let summary chips activate a named tab inside the section they link to.
    // The deal sheet's read-only "Our ARV" chip uses this to open the existing
    // property Calculator without duplicating any calculator controls.
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-tab-target]');
        if (!trigger) return;

        var name = trigger.getAttribute('data-tab-target');
        var scopeSelector = trigger.getAttribute('href');
        if (!name || !scopeSelector || scopeSelector.charAt(0) !== '#') return;

        var scope = document.querySelector(scopeSelector);
        var tab = scope && scope.querySelector('.tabs [data-tab="' + name + '"]');
        if (!tab) return;

        e.preventDefault();
        tab.click();
        scope.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Activate a named tab inside the acqs property details card after enrichment.
    // The server sends HX-Trigger: {"activateAcqsTab": "data"} so the user lands
    // on the Data tab regardless of which tab was active when they clicked Refresh.
    document.body.addEventListener('activateAcqsTab', function (e) {
        var name = typeof e.detail === 'string' ? e.detail : (e.detail && e.detail.value);
        if (!name) return;
        // Find the panel to activate — it carries data-tab-content="<name>" —
        // then click its button so SeeMe's tabs.js owns the class toggling (and
        // fires the tab-changed event the map/comps panes listen for).
        var panel = document.querySelector('[data-tab-content="' + name + '"]');
        var parent = panel && panel.parentNode;
        var tab = parent && parent.querySelector('.tabs [data-tab="' + name + '"]');
        if (tab) tab.click();
    });

    // Remove deleted items from DOM by data-*-id attribute
    ['proposal', 'lead', 'owner', 'affiliate', 'firm'].forEach(function (type) {
        document.body.addEventListener(type + 'Deleted', function (e) {
            var id = e.detail && (e.detail[type + 'Id'] || e.detail.id);
            if (id) {
                document.querySelectorAll('[data-' + type + '-id="' + id + '"]').forEach(function (el) { el.remove(); });
            }
        });
    });

    // Collections case sheet — radio switches textarea placeholder
    document.body.addEventListener('change', function (e) {
        if (e.target.name !== 'send_as' || !e.target.dataset.placeholder) return;
        var footer = e.target.closest('.sheet__footer') || e.target.closest('[data-sheet-footer]');
        if (!footer) return;
        var ta = footer.querySelector('textarea[name="message"]');
        if (ta) ta.placeholder = e.target.dataset.placeholder;
    });

    // A dropdown menu marked data-collapse-reset puts its collapses back to their
    // rendered state on every open (`data-collapse-default="open"` or "closed"), so
    // a confirm left open when the menu closed does not come back
    // (`templates/acqs/_get_contacts_panel.html`). dropdowns.js announces only
    // `dropdown:open`, on the trigger; `detail.menu` is the menu even when portaled.
    document.addEventListener('dropdown:open', function (e) {
        var menu = e.detail && e.detail.menu;
        if (!menu || !menu.hasAttribute('data-collapse-reset')) return;
        menu.querySelectorAll('[data-collapse-default]').forEach(function (el) {
            el.classList.toggle('collapse--open', el.getAttribute('data-collapse-default') === 'open');
        });
    });

})();
